import { createAsyncThunk, createSlice, createEntityAdapter } from '@reduxjs/toolkit';
import axios from 'axios';

export const getCommunicationNotes = createAsyncThunk(
  'backofficeApp/communicationNotes/getCommunicationNotes',
  async ({ params = {}, signal }, { dispatch, rejectWithValue  }) => {
    try {
      const response = await axios.get('/api/communicationNotes', {
        params,
        signal, // AbortController
      });

      const data = response.data.note_notifications || [];
      return data;
    } catch (error) {
      if (axios.isCancel?.(error) || error.name === 'CanceledError') {
        // Send Request canceled message on Aborting to differentiate from other error
        return rejectWithValue('Request canceled');
      }
      return rejectWithValue(error?.response?.data?.message || 'Fetch failed');
    }
  }
);

export const getUnprocessedEmails = createAsyncThunk(
  'backofficeApp/communicationNotes/getUnprocessedEmails',
  async ({ params = {}, signal, append = false }, { dispatch, rejectWithValue  }) => {
    try {
      const response = await axios.get('/api/unprocessedEmails', {
        params,
        signal, // AbortController
      });
      const data = response.data.notes || [];
      const pagination = {
        page: response.data.page ?? params.page ?? 0,
        size: response.data.size ?? params.size ?? data.length,
        totalPages: response.data.total_pages ?? 1,
        totalElements: response.data.total_elements ?? data.length,
        hasNext: response.data.has_next ?? false,
      };
      return { data, pagination, append };
    } catch (error) {
      if (axios.isCancel?.(error) || error.name === 'CanceledError') {
        // Send Request canceled message on Aborting to differentiate from other error
        return rejectWithValue('Request canceled');
      }
      return rejectWithValue(error?.response?.data?.message || 'Fetch failed');
    }
  }
)

export const updateUnprocessedEmails = createAsyncThunk(
  'backofficeApp/communicationNotes/updateUnprocessedEmails',
  async ({id, params}, { dispatch }) => {
    const response = await axios.put(`api/unprocessedEmail/${id}`, params)
    return response.data;
  }
);

/**
 * PGSS-5694: the list after an email is marked completed, changed in place. Re-fetching page 0 instead emptied the
 * list while it loaded (so the page jumped back to the top) and dropped every page loaded with "load more".
 * Without "Include Completed Emails" the email leaves the list; with it, it stays and shows as completed.
 */
export function markUnprocessedEmailCompleted(notifications, id, keepCompleted) {
  const list = Array.isArray(notifications) ? notifications : [];
  if (!keepCompleted) return list.filter((item) => item?.id !== id);
  return list.map((item) => (item?.id === id ? { ...item, status: 'completed' } : item));
}

export const initState = {
    loading: false,
    loadingMore: false,
    notifications: [],
    pagination: {
      page: 0,
      size: 50,
      totalPages: 0,
      totalElements: 0,
      hasNext: false,
    },
};

const communicationNotesAdapter = createEntityAdapter({});

export const { selectAll: selectNotifications, selectById: selectOrderById } =
    communicationNotesAdapter.getSelectors((state) => state.backofficeApp.communicationNotes);

const communicationSlice = createSlice({
    name: 'backofficeApp/communicationNotes',
    initialState: communicationNotesAdapter.getInitialState(initState),
    reducers: {
        setLoading: (state, action) => {
            state.loading = true;
        },
        resetLoading: (state, action) => {
            state.loading = false;
        },
        resetNotifications: () => communicationNotesAdapter.getInitialState({
            ...initState
        }),
    },
    extraReducers: {
        [getCommunicationNotes.pending]: (state, action) => {
            state.notifications = []; // clear on new request
            state.loading = true;
        },
        [getCommunicationNotes.fulfilled]: (state, action) => {
            state.notifications = action.payload;
            state.loading = false;
        },
        [getCommunicationNotes.rejected]: (state, action) => {
            // Don't clear notifications and loading on cancellation
            if (action.payload !== 'Request canceled') {
                state.notifications = [];
                state.loading = false;
            }
        },

        [getUnprocessedEmails.pending]: (state, action) => {
          const append = action?.meta?.arg?.append === true;
          if (!append) {
            state.notifications = [];
            state.loading = true;
            state.loadingMore = false;
          } else {
            state.loadingMore = true;
          }
        },
        [getUnprocessedEmails.fulfilled]: (state, action) => {
          const { data, pagination, append } = action.payload;
          state.notifications = append ? [...state.notifications, ...data] : data;
          state.pagination = pagination;
            state.loading = false;
          state.loadingMore = false;
        },
        [updateUnprocessedEmails.fulfilled]: (state, action) => {
          const { id, params, keepCompleted } = action?.meta?.arg || {};
          if (params?.is_complete !== true) return;
          const before = state.notifications.length;
          state.notifications = markUnprocessedEmailCompleted(state.notifications, id, keepCompleted);
          const removed = before - state.notifications.length;
          if (removed > 0 && state.pagination?.totalElements) {
            state.pagination.totalElements = Math.max(0, state.pagination.totalElements - removed);
          }
        },
        [getUnprocessedEmails.rejected]: (state, action) => {
            // Don't clear notifications and loading on cancellation
            if (action.payload !== 'Request canceled') {
            if (!(action?.meta?.arg?.append === true)) {
              state.notifications = [];
            }
                state.loading = false;
            state.loadingMore = false;
            }
        },
    },
});

export const {
    setLoading,
    resetLoading,
    resetNotifications
} = communicationSlice.actions;

export default communicationSlice.reducer;
